import React, { useState, useEffect } from 'react';
import { getFormFields, addFormField, deleteFormField } from '../services/api';
import { useNavigate } from 'react-router-dom';

const ManageFormFields = () => {
  const [fields, setFields] = useState([]);
  const [newField, setNewField] = useState({ field_name: '', field_type: 'text', section: '', order_num: 0, is_required: false });
  const [error, setError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }

    getFormFields()
      .then(res => setFields(res.data))
      .catch(err => {
        setError(err.response?.data?.error || 'Failed to fetch form fields');
        if (err.response?.status === 401) {
          navigate('/login');
        }
      });
  }, [navigate]);

  const handleAdd = () => {
    setError('');
    addFormField(newField)
      .then(res => {
        setFields([...fields, { ...newField, id: res.data.id }]);
        setNewField({ field_name: '', field_type: 'text', section: '', order_num: 0, is_required: false });
      })
      .catch(err => {
        setError(err.response?.data?.error || 'Failed to add form field');
      });
  };

  const handleDelete = (id) => {
    setError('');
    deleteFormField(id)
      .then(() => setFields(fields.filter(f => f.id !== id)))
      .catch(err => {
        setError(err.response?.data?.error || 'Failed to delete form field');
        if (err.response?.status === 401) {
          navigate('/login');
        }
      });
  };

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <h2 className="text-3xl font-bold text-gray-900 mb-6">Manage Form Fields</h2>
      {error && <p className="text-red-500 mb-4">{error}</p>}
      <div className="bg-white p-6 rounded-xl shadow-md mb-8">
        <h3 className="text-xl font-semibold text-gray-800 mb-4">Add New Field</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <label className="block text-gray-700 font-medium mb-2">Field Name</label>
            <input
              placeholder="Field Name"
              value={newField.field_name}
              onChange={e => setNewField({ ...newField, field_name: e.target.value })}
              className="w-full p-3 border border-gray-300 rounded-md focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500"
            />
          </div>
          <div>
            <label className="block text-gray-700 font-medium mb-2">Field Type</label>
            <select
              value={newField.field_type}
              onChange={e => setNewField({ ...newField, field_type: e.target.value })}
              className="w-full p-3 border border-gray-300 rounded-md focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500"
            >
              <option value="text">Text</option>
              <option value="number">Number</option>
            </select>
          </div>
          <div>
            <label className="block text-gray-700 font-medium mb-2">Section</label>
            <input
              placeholder="Section"
              value={newField.section}
              onChange={e => setNewField({ ...newField, section: e.target.value })}
              className="w-full p-3 border border-gray-300 rounded-md focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500"
            />
          </div>
          <div>
            <label className="block text-gray-700 font-medium mb-2">Order</label>
            <input
              type="number"
              value={newField.order_num}
              onChange={e => setNewField({ ...newField, order_num: parseInt(e.target.value) || 0 })}
              className="w-full p-3 border border-gray-300 rounded-md focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500"
            />
          </div>
          <div>
            <label className="block text-gray-700 font-medium mb-2">Required</label>
            <select
              value={newField.is_required}
              onChange={e => setNewField({ ...newField, is_required: e.target.value === 'true' })}
              className="w-full p-3 border border-gray-300 rounded-md focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500"
            >
              <option value={false}>No</option>
              <option value={true}>Yes</option>
            </select>
          </div>
          <div className="flex items-end">
            <button
              onClick={handleAdd}
              className="bg-indigo-500 text-white px-6 py-3 rounded-md hover:bg-indigo-600 transition-colors"
            >
              Add
            </button>
          </div>
        </div>
      </div>
      <div className="overflow-x-auto bg-white shadow-lg rounded-lg">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-indigo-600">
            <tr>
              <th className="px-6 py-3 text-left text-sm font-semibold text-white">ID</th>
              <th className="px-6 py-3 text-left text-sm font-semibold text-white">Name</th>
              <th className="px-6 py-3 text-left text-sm font-semibold text-white">Type</th>
              <th className="px-6 py-3 text-left text-sm font-semibold text-white">Section</th>
              <th className="px-6 py-3 text-left text-sm font-semibold text-white">Order</th>
              <th className="px-6 py-3 text-left text-sm font-semibold text-white">Required</th>
              <th className="px-6 py-3 text-left text-sm font-semibold text-white">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {fields.map(field => (
              <tr key={field.id} className="hover:bg-gray-50 transition-colors">
                <td className="px-6 py-4 text-sm text-gray-900">{field.id}</td>
                <td className="px-6 py-4 text-sm text-gray-900">{field.field_name}</td>
                <td className="px-6 py-4 text-sm text-gray-900">{field.field_type}</td>
                <td className="px-6 py-4 text-sm text-gray-900">{field.section}</td>
                <td className="px-6 py-4 text-sm text-gray-900">{field.order_num}</td>
                <td className="px-6 py-4 text-sm text-gray-900">{field.is_required ? 'Yes' : 'No'}</td>
                <td className="px-6 py-4 text-sm">
                  <button
                    onClick={() => handleDelete(field.id)}
                    className="bg-red-500 text-white px-4 py-2 rounded-md hover:bg-red-600 transition-colors"
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {fields.length === 0 && <p className="text-gray-600 mt-4">No fields found.</p>}
    </div>
  );
};

export default ManageFormFields;